import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Turno, Clientes } from 'app/interfaces';
import { LoadingComponent } from 'app/components';
import { finalize } from 'rxjs/operators';
import { DatetimeService, TurnoService, DialogService, ClienteService } from 'app/services';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-turnos-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, LoadingComponent, TranslocoModule],
  templateUrl: './turnos-form.component.html',
  styleUrl: './turnos-form.component.css'
})
export class TurnosFormComponent implements OnInit {
  public formularioTurno!: FormGroup;
  public clientes!: Clientes;
  public turnoId!: string | null;
  public turno!: Turno;
  public loading: boolean = true;
  public loadingBotao: boolean = false;
  public botaoDesabilitado: boolean = true;
  public clienteId: number = Number(localStorage.getItem('clienteId'));

  constructor(
    private formBuilder: FormBuilder,
    public turnoService: TurnoService,
    private dialogService: DialogService,
    private clienteService: ClienteService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private datetimeService: DatetimeService,
    private translocoService: TranslocoService
  ) {}

  public ngOnInit(): void {
    this.construirForm();
    this.obterTurnoIdDaRota();
    this.buscarClientes();
    this.atualizarEstadoBotaoSubmit();
  }

  private buscarClientes(): void {
    this.clienteService.buscarClientesUsuario().subscribe((clientes) => {
      this.clientes = clientes;
      this.buscar();
    });
  }

  public buscar(): void {
    if (this.turnoId) {
      this.turnoService.buscar(this.turnoId).subscribe((turno) => {
        this.loading = false;
        turno.tm_hora_inicio = this.datetimeService.HorarioUTCparaBRT(turno.tm_hora_inicio);
        turno.tm_hora_fim = this.datetimeService.HorarioUTCparaBRT(turno.tm_hora_fim);
        this.formularioTurno.patchValue(turno);
        this.formularioTurno.get('fk_cliente')?.disable();
      });
    } else {
      this.formularioTurno.get('fk_cliente')?.setValue(this.clienteId);
      this.loading = false;
    }
  }

  private construirForm(): void {
    this.formularioTurno = this.formBuilder.group(
      {
        fk_cliente: ['', Validators.required],
        vc_nome: ['', Validators.required],
        tm_hora_inicio: ['00:00', Validators.required],
        tm_hora_fim: ['00:00', Validators.required],
      },
      {
        updateOn: 'blur'
      }
    );
  }

  private obterTurnoIdDaRota(): void {
    this.turnoId = this.route.snapshot.paramMap.get('id');
  }

  public atualizar(): void {
    this.loadingBotao = true;

    const turnoData = this.formularioTurno.value;
    turnoData.tm_hora_inicio = this.datetimeService.HorarioBRTparaUTC(turnoData.tm_hora_inicio);
    turnoData.tm_hora_fim = this.datetimeService.HorarioBRTparaUTC(turnoData.tm_hora_fim);
    turnoData.fk_cliente = this.clienteId;

    this.turnoService.atualizar(turnoData, this.turnoId!)
      .pipe(finalize(() => (this.loadingBotao = false)))
      .subscribe({
        next: async () => {
          await this.dialogService.sucesso(
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.successUpdateTitle'),
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.successUpdateMessage')
          );

          this.router.navigate(['premoplan/turnos']);
        },
        error: (err) => {
          if (err.status === 409) {
            this.dialogService.erro(
              this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.conflictTitle'),
              this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.conflictMessage')
            );
            return;
          }

          const mensagem = (err.error?.status || this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.unexpectedError')) + ' ' +
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.contactSupport');

          this.dialogService.erro(
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.errorUpdateTitle'),
            mensagem
          );
        },
      });
  }

  public cadastrar(): void {
    this.loadingBotao = true;

    const turnoData: Turno = this.formularioTurno.value;
    turnoData.tm_hora_inicio = this.datetimeService.HorarioBRTparaUTC(turnoData.tm_hora_inicio);
    turnoData.tm_hora_fim = this.datetimeService.HorarioBRTparaUTC(turnoData.tm_hora_fim);

    this.turnoService.cadastrar(turnoData)
      .pipe(finalize(() => (this.loadingBotao = false)))
      .subscribe({
        next: async () => {
          await this.dialogService.sucesso(
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.successCreateTitle'),
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.successCreateMessage')
          );

          this.router.navigate(['/premoplan/turnos']);
        },
        error: (err) => {
          if (err.status === 409) {
            this.dialogService.erro(
              this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.conflictTitle'),
              this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.conflictMessage')
            );
            return;
          }

          const mensagem = (err.error?.status || this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.unexpectedError')) + ' ' +
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.contactSupport');

          this.dialogService.erro(
            this.translocoService.translate('COMPONENTS.TURNOS_FORM.dialogs.errorCreateTitle'),
            mensagem
          );
        },
      });
  }

  public cancelar(): void {
    this.router.navigate(['/premoplan/turnos']);
  }

  public onSubmit(): void {
    this.turnoId ? this.atualizar() : this.cadastrar();
  }

  public atualizarEstadoBotaoSubmit(): void {
    this.formularioTurno.valueChanges.subscribe(() => {
      const inicio = this.normalizarHorario(this.formularioTurno.get('tm_hora_inicio')?.value);
      const fim = this.normalizarHorario(this.formularioTurno.get('tm_hora_fim')?.value);

      this.botaoDesabilitado =
        !this.formularioTurno.dirty ||
        this.formularioTurno.invalid ||
        inicio === fim;
    });
  }

  private normalizarHorario(horario: string): string {
    return horario.length === 5 ? `${horario}:00` : horario;
  }
}
